'use client';

import { useTranslation } from '@/hooks/useTranslation';
import { FiTrendingUp } from 'react-icons/fi';
import { BiBot, BiDollarCircle, BiRocket, BiSupport, BiTime, BiTrendingUp } from 'react-icons/bi';
import { HiOutlineChartBar, HiOutlineClock, HiOutlineRocketLaunch } from 'react-icons/hi2';
import { HeroBots, IImageOrientation } from '@/components/blocks/HeroBots';
import WhyChooseThisBot, { ColorDefaultChoice } from '@/components/blocks/WhyChooseThisBot';
import AffiliationHowItWorks from '@/components/affiliation/AffiliationHowItWorks';
import AllreadyPaid from '@/components/affiliation/AllreadyPaid';
import SiteFrequentlyAskedQuestionsComponent from '@/components/frequently-asked-questions/SiteFrequentlyAskedQuestionsComponent';
import React from 'react';
import { CtaTargetType } from '@/components/buttons/LouiseButton';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const hero = {
    content: {
      badge: t('AffiliationPrograms/banner/badge'),
      title: t('AffiliationPrograms/banner/title'),
      description: t('AffiliationPrograms/banner/description'),
      cta: t('AffiliationPrograms/banner/cta'),
      img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071611385671_allbots_2.png',
      icon: BiDollarCircle,
      imgOrientation: 'landscape' as IImageOrientation,
      ctaTarget: 'partner-register' as CtaTargetType,
    },
    benefits: [
      { icon: <BiBot className="tw-w-5 tw-h-5" />, text: t('AffiliationPrograms/banner/benefits/benefit1') },
      { icon: <FiTrendingUp className="tw-w-5 tw-h-5" />, text: t('AffiliationPrograms/banner/benefits/benefit2') },
      { icon: <BiRocket className="tw-w-5 tw-h-5" />, text: t('AffiliationPrograms/banner/benefits/benefit3') },
      { icon: <BiSupport className="tw-w-5 tw-h-5" />, text: t('AffiliationPrograms/banner/benefits/benefit4') },
    ],
    stats: [
      { value: '#1', label: t('AffiliationPrograms/banner/stats/stats1') },
      { value: '#4', label: t('AffiliationPrograms/banner/stats/stats2') },
      { value: '30%', label: t('AffiliationPrograms/banner/stats/stats3') },
      { value: '5min', label: t('AffiliationPrograms/banner/stats/stats4') },
    ],
  };

  const sections = t('AffiliationPrograms/whyStart/sections', { returnObjects: true }) as Array<any>;

  const whyChooseThisBot = [
    {
      iconicText: t(sections[0].iconicText),
      title: t(sections[0].title),
      description: t(sections[0].description),
      icon: <BiTrendingUp className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineChartBar className="tw-w-6 tw-h-6 tw-text-purple-600" />,
      metricLabel: t(sections[0].metricLabel),
      button: t(sections[0].button),
      metric: t(sections[0].metric),
      colorFrom: 'pink' as ColorDefaultChoice,
      colorTo: 'purple' as ColorDefaultChoice,
    },
    {
      iconicText: t(sections[1].iconicText),
      title: t(sections[1].title),
      description: t(sections[1].description),
      icon: <BiTime className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineClock className="tw-w-6 tw-h-6 tw-text-blue-600" />,
      metricLabel: t(sections[1].metricLabel),
      button: t(sections[1].button),
      metric: t(sections[1].metric),
      colorFrom: 'blue' as ColorDefaultChoice,
      colorTo: 'cyan' as ColorDefaultChoice,
    },
    {
      iconicText: t(sections[2].iconicText),
      title: t(sections[2].title),
      description: t(sections[2].description),
      icon: <BiRocket className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineRocketLaunch className="tw-w-6 tw-h-6 tw-text-green-600" />,
      metricLabel: t(sections[2].metricLabel),
      button: t(sections[2].button),
      metric: t(sections[2].metric),
      colorFrom: 'green' as ColorDefaultChoice,
      colorTo: 'emerald' as ColorDefaultChoice,
    },
  ];

  return (
    <div>
      {/* Hero Section - Full Width */}
      <HeroBots benefits={hero.benefits} stats={hero.stats} content={hero.content} locale={locale} />

      <AnimatedGradientWrapper>
        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <WhyChooseThisBot
            title={t('AffiliationPrograms/whyStart/title')}
            subtitle={t('AffiliationPrograms/whyStart/subtitle')}
            description={t('AffiliationPrograms/whyStart/description')}
            sections={whyChooseThisBot}
          />
        </section>
        <AffiliationHowItWorks locale={locale} />
        <AllreadyPaid locale={locale} />
        <SiteFrequentlyAskedQuestionsComponent session="AffiliateFAQs" locale={locale} />
      </AnimatedGradientWrapper>
    </div>
  );
}
